import React from 'react';
import PropTypes from 'prop-types';
import useDataSourceContext from '../useDataSourceContext';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { FormattedMessage } from 'react-intl';
import Button from '@material-ui/core/Button';
import ActionContainer from './ActionContainer';
import ContentDrawer from '../../ContentDrawer';
import FilterSelectionItem from './FilterSelectionItem';
import RowCounter from './RowCounter';
import translations from './translations';

const FilterSelection = ({ filterFormatters, filterRenderers }) => {
  const intl = useIntl();
  const {
    showFilterSelection,
    setShowFilterSelection,
    filters: filtersModule = {},
    pagination = {},
  } = useDataSourceContext();
  const {
    clearFilterSelection,
    clearActiveFilters,
    activeFilters,
    filterSelection,
    hasAvailableFilters,
  } = filtersModule;
  const { itemCount } = pagination;

  /**
   * Handle closing the filter selection drawer.
   */
  const handleCloseFilters = () => {
    setShowFilterSelection(false);
    clearFilterSelection();
  };

  /**
   * Reset the selected filters to the filters of the default view.
   */
  const handleResetSelectedFilters = () => {
    clearActiveFilters();
  };

  /**
   * Return a single filter selection element.
   * @param {Object|null} filter
   * @returns {Component}
   */
  const getDataGridFilterSelectionItem = filter => {
    let isOpen = false;
    const isNewFilter = filterSelection && filterSelection.isNew && filter.isNew;
    const isCurrentSelection = filterSelection && filterSelection.currentKey === filter.key;

    if (filterSelection && (isCurrentSelection || isNewFilter)) {
      isOpen = true;
    }

    return (
      <FilterSelectionItem
        key={filter.key}
        filter={filter}
        filterFormatters={filterFormatters}
        filterRenderers={filterRenderers}
        isOpen={isOpen}
      />
    );
  };

  return (
    <ContentDrawer
      drawerWidth="600px"
      isOpen={showFilterSelection}
      handleClose={handleCloseFilters}
      title={intl.formatMessage(translations.title)}
    >
      <ActionContainer>
        <Button variant="outlined" onClick={handleResetSelectedFilters}>
          <FormattedMessage {...translations.resetButton} />
        </Button>
        <RowCounter>
          <FormattedMessage
            {...translations.rowCount}
            values={{
              count: itemCount,
              total:
                itemCount !== undefined
                  ? itemCount
                  : intl.formatMessage({
                      id: 'dataGrid.pagination.many',
                      defaultMessage: 'many',
                      description: 'Label when the total number of rows are unknown',
                    }),
            }}
          />
        </RowCounter>
      </ActionContainer>
      {activeFilters.map(activeFilter => getDataGridFilterSelectionItem(activeFilter))}
      {hasAvailableFilters() &&
        getDataGridFilterSelectionItem({ key: 'create-filter', type: 'new', isNew: true })}
    </ContentDrawer>
  );
};

FilterSelection.propTypes = {
  filterFormatters: PropTypes.object,
  filterRenderers: PropTypes.object,
};

FilterSelection.defaultProps = {
  filterFormatters: {},
  filterRenderers: {},
};

export default FilterSelection;
